import {
  formatSocialSecurityNumber,
  normalizeSocialSecurityNumber,
  SOCIAL_SECURITY_NUMBER_SEPARATOR,
} from '@omecare/utils';

/** Characters a NIR is made of: digits, plus the Corsica department letters. */
const ALLOWED_CHARACTERS = /[^0-9AB]/g;

/** Result of masking one edit: what to display, and where to put the caret. */
export type MaskedSocialSecurityNumber = {
  value: string;
  caretPosition: number;
};

/**
 * Reduces a displayed value to the characters the mask actually carries,
 * dropping separators and anything a NIR cannot contain.
 *
 * @param value - a displayed or partially typed value
 * @returns the significant characters only
 */
const toSignificantCharacters = (value: string): string =>
  normalizeSocialSecurityNumber(value).replace(ALLOWED_CHARACTERS, '');

/**
 * Locates, inside a formatted value, the caret position that sits after
 * `characterCount` significant characters.
 *
 * The caret lands *after* any separator that follows them, so typing the last
 * character of a group leaves the caret ready for the next one.
 *
 * @param params.formattedValue - the grouped value being displayed
 * @param params.characterCount - how many significant characters precede the caret
 * @returns the caret offset within `formattedValue`
 */
const caretPositionAfter = ({
  formattedValue,
  characterCount,
}: {
  formattedValue: string;
  characterCount: number;
}): number => {
  let seen = 0;
  for (let index = 0; index < formattedValue.length; index += 1) {
    if (formattedValue[index] === SOCIAL_SECURITY_NUMBER_SEPARATOR) {
      continue;
    }
    if (seen === characterCount) {
      return index;
    }
    seen += 1;
  }
  return formattedValue.length;
};

/**
 * Applies the NIR display mask to one edit of the input.
 *
 * Beyond regrouping, it fixes the two behaviours a naive mask gets wrong:
 * backspacing onto a separator deletes the character before it rather than
 * doing nothing, and the caret is re-anchored to the character it was next to
 * instead of jumping to the end.
 *
 * @param params.editedValue - the input value right after the browser applied the keystroke
 * @param params.caretPosition - the caret offset the browser left in `editedValue`
 * @param params.previousValue - the masked value displayed before this edit
 * @param params.isBackspace - whether the edit was a backward deletion
 * @returns the value to display and the caret position to restore
 * @example
 * // backspacing at "2 69 |05" removes the 9, not just the separator
 * maskSocialSecurityNumberChange({
 *   editedValue: '2 6905', caretPosition: 4, previousValue: '2 69 05', isBackspace: true,
 * }); // { value: '2 60 5', caretPosition: 3 }
 */
export const maskSocialSecurityNumberChange = ({
  editedValue,
  caretPosition,
  previousValue,
  isBackspace,
}: {
  editedValue: string;
  caretPosition: number;
  previousValue: string;
  isBackspace: boolean;
}): MaskedSocialSecurityNumber => {
  let characters = toSignificantCharacters(editedValue);
  let characterCaret = Math.min(
    toSignificantCharacters(editedValue.slice(0, caretPosition)).length,
    characters.length,
  );

  // A backspace that left the significant characters untouched only swallowed a
  // separator — delete the character it was meant to reach instead
  const deletedOnlyASeparator =
    isBackspace &&
    characters.length === toSignificantCharacters(previousValue).length;
  if (deletedOnlyASeparator && characterCaret > 0) {
    characters =
      characters.slice(0, characterCaret - 1) +
      characters.slice(characterCaret);
    characterCaret -= 1;
  }

  const value = formatSocialSecurityNumber(characters);
  return {
    value,
    caretPosition: caretPositionAfter({
      formattedValue: value,
      characterCount: characterCaret,
    }),
  };
};
